<template>
  <!-- The picker's menu must escape the body only on the short pick step; the create step is a form that may scroll -->
  <Modal
    :content-class="step === 'pick' ? 'team-picker-dialog' : undefined"
    ref="modalRef"
    width="30rem"
    @after-close="reset"
  >
    <template #header>{{ step === 'create' ? 'Create new agent' : 'Add a showing agent' }}</template>

    <template #body>
      <View v-if="step === 'pick'" direction="column" gap="md" class="form">
        <Text variant="label-medium">Showing agent</Text>

        <MenuButton full-width>
          <template #menu-button-trigger>
            <PickerTrigger :label="pickedName || 'Choose your showing agent'" :selected="Boolean(choice)" />
          </template>

          <div v-if="teammates.length" role="group" aria-label="Your team" class="menu-group">
            <Text variant="label-small" color="tertiary" class="menu-group-title" aria-hidden="true">Your team</Text>
            <MenuButtonItem
              v-for="member in teammates"
              :key="member.investorId"
              :title="member.isYou ? `${nameOf(member)} (you)` : nameOf(member)"
              :subtitle="roleOf(member)"
              :is-active="choice?.kind === 'teammate' && choice.id === member.investorId"
              @click="choice = { kind: 'teammate', id: member.investorId }"
            >
              <template #prefix>
                <Avatar :user-name="nameOf(member)" show-single-letter size="small" />
              </template>
            </MenuButtonItem>
          </div>

          <div v-if="contacts.length" role="group" aria-label="Showing agents" class="menu-group">
            <Text variant="label-small" color="tertiary" class="menu-group-title" aria-hidden="true">
              Showing agents
            </Text>
            <MenuButtonItem
              v-for="agent in contacts"
              :key="agent.id"
              :title="getFullName(agent).trim()"
              :subtitle="agent.phone ? formatPhoneNumber(agent.phone) : 'No phone number yet'"
              :is-active="choice?.kind === 'contact' && choice.id === agent.id"
              @click="choice = { kind: 'contact', id: agent.id }"
            >
              <template #prefix>
                <Avatar :user-name="getFullName(agent).trim()" show-single-letter size="small" />
              </template>
            </MenuButtonItem>
          </div>

          <template #menu-footer>
            <MenuButtonItem title="Add new agent" @click="step = 'create'">
              <template #prefix>
                <AvatarPlaceholder icon="plus" />
              </template>
            </MenuButtonItem>
          </template>
        </MenuButton>

        <Hint icon="info-circle">
          Renters get the host's name and number once a tour is confirmed.
          <template v-if="portfolioName">{{ portfolioNote }}</template>
        </Hint>
      </View>

      <FormGroup v-else gap="medium" class="form">
        <div class="name-fields">
          <FormField label="First name" :error="form.fieldErrors('firstName')">
            <Input v-model="form.fields.firstName" autocomplete="off" />
          </FormField>
          <FormField label="Last name" :error="form.fieldErrors('lastName')">
            <Input v-model="form.fields.lastName" autocomplete="off" />
          </FormField>
        </div>

        <FormField label="Phone number" :error="form.fieldErrors('phone')">
          <InputPhone v-model="form.fields.phone" />
        </FormField>

        <Hint icon="info-circle">
          Renters get this number for their tour, and Tallo texts it if a renter cancels or can't get in. They won't get
          a Tallo account or see this property.
          <template v-if="portfolioName">{{ portfolioNote }}</template>
        </Hint>
      </FormGroup>
    </template>

    <template #footer>
      <template v-if="step === 'pick'">
        <Button variant="ghost" @click="close">Cancel</Button>
        <Button :loading="isLoading" :disabled="!choice" @click="addPicked">Add agent</Button>
      </template>
      <template v-else>
        <Button variant="ghost" @click="step = 'pick'">Back</Button>
        <Button :loading="isLoading || form.submitting" @click="createAndAdd">Add agent</Button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { computed, reactive, ref } from 'vue';

import {
  Avatar,
  AvatarPlaceholder,
  Button,
  FormField,
  FormGroup,
  Hint,
  Input,
  InputPhone,
  MenuButton,
  MenuButtonItem,
  Modal,
  PickerTrigger,
  Text,
  View,
  phoneNumberValidator,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { CompanyTeamMemberDto, companyRoleLabels, teamMemberName as nameOf } from '@tallo/investor/company';
import {
  AddPropertyShowingAgentDto,
  PropertyShowingAgentDto,
  ShowingAgentDto,
  useAddPropertyShowingAgent,
} from '@tallo/investor/showing-agents';
import { escapeHtml, formatPhoneNumber, getFullName } from '@tallo/utility';

import { TeamSubject } from '../../dtos/property-team.dto.interface';

type Choice = { kind: 'teammate'; id: string } | { kind: 'contact'; id: string };

const props = withDefaults(
  defineProps<{
    subject: TeamSubject;
    members: CompanyTeamMemberDto[];
    roster: ShowingAgentDto[];
    listed: PropertyShowingAgentDto[];
    /** What each seat already is on this unit - "Manager", "Collaborator" - in place of their company role */
    rolesHere?: Record<string, string>;
    /** The portfolio whose other units an add also reaches */
    portfolioName?: string;
  }>(),
  { rolesHere: () => ({}) },
);

const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const step = ref<'pick' | 'create'>('pick');
const choice = ref<Choice>();
const { mutateAsync, isLoading } = useAddPropertyShowingAgent();

const blankContact = { firstName: '', lastName: '', phone: '' };
const fields = reactive({ ...blankContact });
const form = useForm(fields, {
  firstName: { required: helpers.withMessage('First name is required', required) },
  lastName: { required: helpers.withMessage('Last name is required', required) },
  phone: { phoneNumberValidator, required: helpers.withMessage('Phone number is required', required) },
});

// Teammates already listed here, through any roster entry of theirs, are not offered again
const teammates = computed(() =>
  props.members.filter((member) => !props.listed.some((agent) => agent.investorId === member.investorId)),
);
// A teammate's roster copy is offered as the teammate, so only contacts outside the team appear here
const contacts = computed(() =>
  props.roster.filter((agent) => !agent.investorId && !props.listed.some((listed) => listed.id === agent.id)),
);

const pickedName = computed(() => {
  const picked = choice.value;

  if (picked?.kind === 'teammate') {
    const member = props.members.find(({ investorId }) => investorId === picked.id);

    return member ? nameOf(member) : '';
  }

  if (picked?.kind === 'contact') {
    const agent = props.roster.find(({ id }) => id === picked.id);

    return agent ? getFullName(agent).trim() : '';
  }

  return '';
});

const portfolioNote = computed(
  () => `They're added to the other units of ${props.portfolioName} too, so you don't have to do it unit by unit.`,
);

function roleOf(member: CompanyTeamMemberDto): string {
  return props.rolesHere[member.investorId] ?? companyRoleLabels[member.role];
}

function open() {
  reset();
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reset() {
  step.value = 'pick';
  choice.value = undefined;
  Object.assign(fields, blankContact);
  form.resetValidation();
}

async function addPicked() {
  const picked = choice.value;

  if (!picked) {
    return;
  }

  // Taken before the request, so a dialog dismissed mid-flight still names who was added
  const name = pickedName.value;

  try {
    await submit(picked.kind === 'teammate' ? { investorId: picked.id } : { showingAgentId: picked.id });
  } catch {
    // The mutation already showed the server's message; the dialog stays open for another try
    return;
  }

  close();
  alertsStore.success(`<b>${escapeHtml(name)}</b> added as a showing agent`);
}

/** Someone typed in here is here to be listed, so they are created and listed in one go, with nothing to confirm after */
async function createAndAdd() {
  const name = `${fields.firstName} ${fields.lastName}`.trim();

  try {
    await form.submit(async (contact) => {
      await submit({ ...contact });
    });
  } catch {
    // A field the form or the server rejected shows on the field; the mutation toasted anything else
    return;
  }

  close();
  alertsStore.success(`<b>${escapeHtml(name)}</b> added as a showing agent`);
}

async function submit(agent: AddPropertyShowingAgentDto) {
  await mutateAsync({ subject: props.subject, agent });
}

defineExpose({ open });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.form {
  width: 100%;
}

.menu-group {
  display: flex;
  flex-direction: column;
}

.menu-group-title {
  padding: var(--t-spacing-sm) var(--t-spacing-md) var(--t-spacing-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.name-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--t-spacing-md);

  @include mobile {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
